import { useEffect, useMemo, useState } from "react";
import { api, gbp, formatApiError, fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Trash2, Plus, Search, Fuel, Download, Pencil, ChevronRight, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const UNITS = [
  { v: "per_job",    label: "per job" },
  { v: "per_day",    label: "per day" },
  { v: "per_driver", label: "per driver" },
  { v: "per_hour",   label: "per hour" },
  { v: "per_week",   label: "per week" },
  { v: "per_mile",   label: "per mile" },
];
const unitLabel = (u) => UNITS.find(x => x.v === u)?.label || u;

const emptyForm = () => ({
  client_id: "",
  site_id: "",
  is_general: false,
  contact_type: "subcontractor",
  contact_id: "",
  pay_rate: "",
  unit: "per_day",
  fuel_included: false,
  fuel_per_day: "",
  notes: "",
});

export default function FinancePayable() {
  const [groups, setGroups] = useState([]);   // [{ site_id, site_name, client_name, rates: [...] }]
  const [clients, setClients] = useState([]);
  const [allSites, setAllSites] = useState([]); // [{id, client_id, name}]
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const load = async () => {
    setLoading(true);
    try {
      const [pr, cl, ct] = await Promise.all([
        api.get("/finance/payable-rates"),
        api.get("/clients"),
        api.get("/contacts"),
      ]);
      setGroups(pr.data.sites || []);
      setClients(cl.data || []);
      setContacts(ct.data || []);
      // Fetch sites for each client in parallel
      const sitesArrays = await Promise.all(
        (cl.data || []).map(c => api.get(`/clients/${c.id}/sites`).then(r => r.data.map(s => ({ ...s, client_id: c.id }))).catch(() => []))
      );
      setAllSites(sitesArrays.flat());
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const subOptions = useMemo(() => contacts.filter(c => c.type === "customer" && c.is_active !== false), [contacts]);
  const seOptions  = useMemo(() => contacts.filter(c => c.type === "subcontractor" && c.is_active !== false), [contacts]);
  const sitesOfSelectedClient = useMemo(
    () => allSites.filter(s => s.client_id === form.client_id),
    [allSites, form.client_id]
  );

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    // Group by CLIENT (matches the Chargeable page). Each client card lists all
    // rates for that client's sites — including "General" ones with no contact.
    const byClient = new Map();
    // Seed every client (even ones with no rates yet) so the dropdown shows up.
    for (const cl of clients) {
      byClient.set(cl.id, {
        client_id: cl.id,
        client_name: cl.name,
        rows: [],
      });
    }
    for (const g of groups) {
      const cid = g.client_id;
      if (!cid) continue;
      if (!byClient.has(cid)) {
        byClient.set(cid, { client_id: cid, client_name: g.client_name || "—", rows: [] });
      }
      for (const r of (g.rates || [])) {
        const isGeneral = !(r.contact_id || "").trim() || r.contact_type === "general";
        byClient.get(cid).rows.push({
          ...r,
          site_id: g.site_id,
          site_name: g.site_name,
          _is_general: isGeneral,
        });
      }
    }
    let arr = Array.from(byClient.values()).sort((a, b) =>
      (a.client_name || "").localeCompare(b.client_name || ""),
    );
    if (q) {
      arr = arr.filter((c) =>
        (c.client_name || "").toLowerCase().includes(q) ||
        c.rows.some((r) =>
          (r.site_name || "").toLowerCase().includes(q) ||
          (r.contact_name || "").toLowerCase().includes(q),
        ),
      );
    } else {
      // Hide clients with zero rates when not searching, so the page stays useful.
      arr = arr.filter((c) => c.rows.length > 0);
    }
    return arr;
  }, [groups, clients, search]);

  // Collapsed by default — expand a client to see its pay rates. When a search
  // filter is active every group auto-opens so matches are visible.
  const [expanded, setExpanded] = useState({});
  const isOpen = (cid) => {
    if (search.trim()) return true;
    return expanded[cid] ?? false;
  };
  const toggle = (cid) => setExpanded((p) => ({ ...p, [cid]: !isOpen(cid) }));

  // Secondary view — group the same rates by the individual sub / driver
  // so ops can see everything owed to each person at a glance. General
  // (unassigned) rates are excluded since they aren't tied to a contact.
  const byContact = useMemo(() => {
    const q = search.trim().toLowerCase();
    const subs = new Map();
    const selfEmp = new Map();
    for (const g of groups) {
      for (const r of (g.rates || [])) {
        const isGeneral = !(r.contact_id || "").trim() || r.contact_type === "general";
        if (isGeneral) continue;
        const bucket = r.contact_type === "self_employed" ? selfEmp : subs;
        const key = r.contact_id || r.contact_name || "__none";
        if (!bucket.has(key)) {
          bucket.set(key, {
            contact_id: r.contact_id,
            contact_name: r.contact_name || "(unknown)",
            contact_type: r.contact_type,
            rows: [],
          });
        }
        bucket.get(key).rows.push({
          ...r,
          site_id: g.site_id,
          site_name: g.site_name,
          client_name: g.client_name,
        });
      }
    }
    const sortByName = (a, b) => (a.contact_name || "").localeCompare(b.contact_name || "");
    let subArr = Array.from(subs.values()).sort(sortByName);
    let selfArr = Array.from(selfEmp.values()).sort(sortByName);
    if (q) {
      const matchesRow = (r) =>
        (r.site_name || "").toLowerCase().includes(q) ||
        (r.client_name || "").toLowerCase().includes(q);
      subArr = subArr.filter((c) => (c.contact_name || "").toLowerCase().includes(q) || c.rows.some(matchesRow));
      selfArr = selfArr.filter((c) => (c.contact_name || "").toLowerCase().includes(q) || c.rows.some(matchesRow));
    }
    return { subs: subArr, selfEmp: selfArr };
  }, [groups, search]);

  const openAdd = () => { setEditingId(null); setForm(emptyForm()); setOpen(true); };

  const openEdit = (r) => {
    // Need to find which client/site this rate belongs to via groups data
    // r already has site_name + client_name, but we need ids — search allSites by site_id.
    const site = allSites.find(s => s.id === r.site_id);
    const isGeneral = !(r.contact_id || "").trim() || r.contact_type === "general";
    setEditingId(r.id);
    setForm({
      client_id: site?.client_id || "",
      site_id: r.site_id,
      is_general: isGeneral,
      contact_type: isGeneral ? "general" : (r.contact_type || "subcontractor"),
      contact_id: r.contact_id || "",
      pay_rate: String(r.pay_rate ?? ""),
      unit: r.unit || "per_day",
      fuel_included: !!r.fuel_included,
      fuel_per_day: String(r.fuel_per_day ?? ""),
      notes: r.notes || "",
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.client_id) return toast.error("Pick a client");
    if (!form.site_id)   return toast.error("Pick a site");
    if (!form.is_general && !form.contact_id) return toast.error("Pick a contact or switch on General");
    if (!form.pay_rate)  return toast.error("Enter a pay rate");
    const payload = {
      contact_id: form.is_general ? "" : form.contact_id,
      contact_type: form.is_general ? "general" : form.contact_type,
      pay_rate: parseFloat(form.pay_rate),
      unit: form.unit,
      fuel_included: !!form.fuel_included,
      fuel_per_day: form.fuel_included ? parseFloat(form.fuel_per_day || 0) : 0,
      notes: form.notes || "",
    };
    try {
      if (editingId) {
        await api.put(`/site-rates/${editingId}`, payload);
        toast.success("Pay rate updated");
      } else {
        await api.post(`/sites/${form.site_id}/rates`, payload);
        toast.success("Pay rate added");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteRate = async (rate_id) => {
    if (!window.confirm("Delete this pay rate?")) return;
    try {
      await api.delete(`/site-rates/${rate_id}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const contactOptions = form.contact_type === "subcontractor" ? subOptions : seOptions;

  return (
    <div data-testid="finance-payable-page" className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-amber-700 font-semibold">Payable</div>
          <h1 className="font-display text-3xl tracking-tight font-semibold text-slate-900 mt-2">Pay rates per site</h1>
          <p className="text-sm text-slate-600 mt-1">Reference card — how much you pay each subcontractor or self-employed driver when they work a specific site. For guidance only.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="add-pay-rate-btn"
            onClick={openAdd}
            className="rounded-sm bg-slate-900 hover:bg-slate-800"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Add pay rate
          </Button>
        </div>
      </div>

      <Card className="rounded-sm border border-slate-200 shadow-none p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="payable-search"
            placeholder="Search by client, site or contact"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 rounded-sm border-slate-300"
          />
        </div>
      </Card>

      {loading && <div className="text-sm text-slate-500">Loading…</div>}

      {!loading && filteredGroups.length === 0 && (
        <Card className="rounded-sm border border-dashed border-slate-300 shadow-none p-12 text-center">
          <div className="text-sm text-slate-700 font-medium">No pay rates yet.</div>
          <div className="text-xs text-slate-500 mt-1">Click <span className="font-medium">+ Add pay rate</span> to create your first entry.</div>
        </Card>
      )}

      {filteredGroups.map((c) => {
        const open = isOpen(c.client_id);
        const generalCount = c.rows.filter((r) => r._is_general).length;
        return (
          <Card key={c.client_id} className="rounded-sm border border-slate-200 shadow-none" data-testid={`client-card-${c.client_id}`}>
            <button
              type="button"
              onClick={() => toggle(c.client_id)}
              data-testid={`client-toggle-${c.client_id}`}
              className="w-full px-5 py-3 border-b border-slate-100 flex items-center justify-between hover:bg-slate-50/60 transition text-left"
            >
              <div className="flex items-center gap-2 min-w-0">
                {open
                  ? <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" />
                  : <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />}
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500">Client</div>
                  <div className="text-sm font-medium text-slate-900 truncate">{c.client_name}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {generalCount > 0 && (
                  <Badge variant="secondary" className="rounded-sm bg-amber-100 text-amber-800 border-amber-200">
                    {generalCount} general
                  </Badge>
                )}
                <Badge variant="secondary" className="rounded-sm" data-testid={`client-badge-${c.client_id}`}>
                  {c.rows.length} {c.rows.length === 1 ? "rate" : "rates"}
                </Badge>
              </div>
            </button>
            {open && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 uppercase text-[11px] tracking-wider">
                    <tr>
                      <th className="text-left px-5 py-2.5">Site</th>
                      <th className="text-left px-5 py-2.5">Assigned to</th>
                      <th className="text-right px-5 py-2.5">Pay rate</th>
                      <th className="text-right px-5 py-2.5">Fuel</th>
                      <th className="text-left px-5 py-2.5">Notes</th>
                      <th className="text-right px-5 py-2.5 w-20"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {c.rows.map((r) => (
                      <tr key={r.id} className={`border-t border-slate-100 ${r._is_general ? "bg-amber-50/40" : ""}`} data-testid={`rate-row-${r.id}`}>
                        <td className="px-5 py-2.5 font-medium">{r.site_name}</td>
                        <td className="px-5 py-2.5">
                          {r._is_general ? (
                            <span className="inline-flex items-center gap-1.5">
                              <span className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold px-1.5 py-0.5 rounded-sm bg-amber-100 border border-amber-200">General</span>
                              <span className="text-slate-600">Unassigned</span>
                            </span>
                          ) : (
                            <span>
                              <span className="text-slate-900">{r.contact_name}</span>
                              <span className="text-[10px] text-slate-500 ml-1.5">({r.contact_type === "subcontractor" ? "sub" : "self-emp"})</span>
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-2.5 text-right tabular-nums font-semibold">
                          {gbp(r.pay_rate)} <span className="text-xs text-slate-500 font-normal">/ {unitLabel(r.unit)}</span>
                        </td>
                        <td className="px-5 py-2.5 text-right tabular-nums">
                          {r.fuel_included ? (
                            <span className="inline-flex items-center gap-1 text-amber-700">
                              <Fuel className="w-3.5 h-3.5" />
                              {gbp(r.fuel_per_day || 0)}<span className="text-xs text-slate-500 font-normal">/day</span>
                            </span>
                          ) : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-5 py-2.5 text-slate-600">{r.notes || "—"}</td>
                        <td className="px-5 py-2.5 text-right">
                          <div className="inline-flex items-center gap-1">
                            <Button
                              variant="ghost" size="icon"
                              data-testid={`rate-edit-${r.id}`}
                              onClick={() => openEdit(r)}
                              className="h-8 w-8 text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                            ><Pencil className="w-4 h-4" /></Button>
                            <Button
                              variant="ghost" size="icon"
                              data-testid={`rate-delete-${r.id}`}
                              onClick={() => deleteRate(r.id)}
                              className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                            ><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        );
      })}

      {/* Break-down by contact — one section for Subcontractors, one for Self-employed drivers.
          Each person is a collapsible card showing which client / site rates apply to them. */}
      {(byContact.subs.length > 0 || byContact.selfEmp.length > 0) && (
        <div className="pt-4 space-y-6">
          {byContact.subs.length > 0 && (
            <section className="space-y-3" data-testid="section-subcontractors">
              <div className="flex items-baseline gap-3">
                <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Break-down · Subcontractors</div>
                <span className="text-[11px] text-slate-500">Every rate assigned to a company subcontractor.</span>
              </div>
              {byContact.subs.map((c) => {
                const key = `sub:${c.contact_id || c.contact_name}`;
                const open = isOpen(key);
                return (
                  <Card key={key} className="rounded-sm border border-slate-200 shadow-none" data-testid={`sub-card-${c.contact_id || c.contact_name}`}>
                    <button
                      type="button"
                      onClick={() => toggle(key)}
                      data-testid={`sub-toggle-${c.contact_id || c.contact_name}`}
                      className="w-full px-5 py-3 border-b border-slate-100 flex items-center justify-between hover:bg-slate-50/60 transition text-left"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {open ? <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />}
                        <div className="min-w-0">
                          <div className="text-[11px] uppercase tracking-wider text-slate-500">Subcontractor</div>
                          <div className="text-sm font-medium text-slate-900 truncate">{c.contact_name}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                        <Badge variant="secondary" className="rounded-sm">{c.rows.length} {c.rows.length === 1 ? "site" : "sites"}</Badge>
                        {c.contact_id && (
                          <a
                            data-testid={`sub-pdf-${c.contact_id}`}
                            href={fileUrl(`/finance/payable/${c.contact_id}/pdf`)}
                            download
                            rel="noopener"
                            className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-50"
                          >
                            <Download className="w-3.5 h-3.5" /> PDF
                          </a>
                        )}
                      </div>
                    </button>
                    {open && (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 text-slate-600 uppercase text-[11px] tracking-wider">
                            <tr>
                              <th className="text-left px-5 py-2.5">Client</th>
                              <th className="text-left px-5 py-2.5">Site</th>
                              <th className="text-right px-5 py-2.5">Pay rate</th>
                              <th className="text-right px-5 py-2.5">Fuel</th>
                              <th className="text-left px-5 py-2.5">Notes</th>
                              <th className="text-right px-5 py-2.5 w-20"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {c.rows.map((r) => (
                              <tr key={r.id} className="border-t border-slate-100">
                                <td className="px-5 py-2.5 text-slate-700">{r.client_name}</td>
                                <td className="px-5 py-2.5 font-medium">{r.site_name}</td>
                                <td className="px-5 py-2.5 text-right tabular-nums font-semibold">
                                  {gbp(r.pay_rate)} <span className="text-xs text-slate-500 font-normal">/ {unitLabel(r.unit)}</span>
                                </td>
                                <td className="px-5 py-2.5 text-right tabular-nums">
                                  {r.fuel_included ? (
                                    <span className="inline-flex items-center gap-1 text-amber-700">
                                      <Fuel className="w-3.5 h-3.5" />
                                      {gbp(r.fuel_per_day || 0)}<span className="text-xs text-slate-500 font-normal">/day</span>
                                    </span>
                                  ) : <span className="text-slate-400">—</span>}
                                </td>
                                <td className="px-5 py-2.5 text-slate-600">{r.notes || "—"}</td>
                                <td className="px-5 py-2.5 text-right">
                                  <div className="inline-flex items-center gap-1">
                                    <Button
                                      variant="ghost" size="icon"
                                      data-testid={`sub-rate-edit-${r.id}`}
                                      onClick={() => openEdit(r)}
                                      className="h-8 w-8 text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                                    ><Pencil className="w-4 h-4" /></Button>
                                    <Button
                                      variant="ghost" size="icon"
                                      data-testid={`sub-rate-delete-${r.id}`}
                                      onClick={() => deleteRate(r.id)}
                                      className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                    ><Trash2 className="w-4 h-4" /></Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </Card>
                );
              })}
            </section>
          )}
          {byContact.selfEmp.length > 0 && (
            <section className="space-y-3" data-testid="section-selfemployed">
              <div className="flex items-baseline gap-3">
                <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Break-down · Self-employed drivers</div>
                <span className="text-[11px] text-slate-500">Every rate assigned to a self-employed driver.</span>
              </div>
              {byContact.selfEmp.map((c) => {
                const key = `self:${c.contact_id || c.contact_name}`;
                const open = isOpen(key);
                return (
                  <Card key={key} className="rounded-sm border border-slate-200 shadow-none" data-testid={`self-card-${c.contact_id || c.contact_name}`}>
                    <button
                      type="button"
                      onClick={() => toggle(key)}
                      data-testid={`self-toggle-${c.contact_id || c.contact_name}`}
                      className="w-full px-5 py-3 border-b border-slate-100 flex items-center justify-between hover:bg-slate-50/60 transition text-left"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {open ? <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />}
                        <div className="min-w-0">
                          <div className="text-[11px] uppercase tracking-wider text-slate-500">Self-employed</div>
                          <div className="text-sm font-medium text-slate-900 truncate">{c.contact_name}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                        <Badge variant="secondary" className="rounded-sm">{c.rows.length} {c.rows.length === 1 ? "site" : "sites"}</Badge>
                        {c.contact_id && (
                          <a
                            data-testid={`self-pdf-${c.contact_id}`}
                            href={fileUrl(`/finance/payable/${c.contact_id}/pdf`)}
                            download
                            rel="noopener"
                            className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-50"
                          >
                            <Download className="w-3.5 h-3.5" /> PDF
                          </a>
                        )}
                      </div>
                    </button>
                    {open && (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 text-slate-600 uppercase text-[11px] tracking-wider">
                            <tr>
                              <th className="text-left px-5 py-2.5">Client</th>
                              <th className="text-left px-5 py-2.5">Site</th>
                              <th className="text-right px-5 py-2.5">Pay rate</th>
                              <th className="text-right px-5 py-2.5">Fuel</th>
                              <th className="text-left px-5 py-2.5">Notes</th>
                              <th className="text-right px-5 py-2.5 w-20"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {c.rows.map((r) => (
                              <tr key={r.id} className="border-t border-slate-100">
                                <td className="px-5 py-2.5 text-slate-700">{r.client_name}</td>
                                <td className="px-5 py-2.5 font-medium">{r.site_name}</td>
                                <td className="px-5 py-2.5 text-right tabular-nums font-semibold">
                                  {gbp(r.pay_rate)} <span className="text-xs text-slate-500 font-normal">/ {unitLabel(r.unit)}</span>
                                </td>
                                <td className="px-5 py-2.5 text-right tabular-nums">
                                  {r.fuel_included ? (
                                    <span className="inline-flex items-center gap-1 text-amber-700">
                                      <Fuel className="w-3.5 h-3.5" />
                                      {gbp(r.fuel_per_day || 0)}<span className="text-xs text-slate-500 font-normal">/day</span>
                                    </span>
                                  ) : <span className="text-slate-400">—</span>}
                                </td>
                                <td className="px-5 py-2.5 text-slate-600">{r.notes || "—"}</td>
                                <td className="px-5 py-2.5 text-right">
                                  <div className="inline-flex items-center gap-1">
                                    <Button
                                      variant="ghost" size="icon"
                                      data-testid={`self-rate-edit-${r.id}`}
                                      onClick={() => openEdit(r)}
                                      className="h-8 w-8 text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                                    ><Pencil className="w-4 h-4" /></Button>
                                    <Button
                                      variant="ghost" size="icon"
                                      data-testid={`self-rate-delete-${r.id}`}
                                      onClick={() => deleteRate(r.id)}
                                      className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                    ><Trash2 className="w-4 h-4" /></Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </Card>
                );
              })}
            </section>
          )}
        </div>
      )}

      <p className="text-[11px] text-slate-500">For guidance only — these rates are reference info for your team, not used in PO or Self-Bill totals.</p>

      {/* Add dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl rounded-sm" data-testid="add-pay-rate-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit pay rate" : "Add pay rate"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* General toggle — when on, this is a baseline payable rate for the
                site that isn't tied to any specific subcontractor / driver yet. */}
            <div className="rounded-sm border border-amber-200 bg-amber-50/60 p-3 flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium text-slate-900">General (no specific contact)</Label>
                <p className="text-[11px] text-slate-500 mt-0.5">Add a baseline pay rate for this site without assigning a subcontractor or self-employed driver.</p>
              </div>
              <Switch
                data-testid="dlg-general-toggle"
                checked={!!form.is_general}
                onCheckedChange={(v) => setForm(f => ({ ...f, is_general: v, contact_id: v ? "" : f.contact_id, contact_type: v ? "general" : (f.contact_type === "general" ? "subcontractor" : f.contact_type) }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-600">Client</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm(f => ({ ...f, client_id: v, site_id: "" }))} disabled={!!editingId}>
                  <SelectTrigger data-testid="dlg-client" className="rounded-sm border-slate-300 mt-1"><SelectValue placeholder="Pick client" /></SelectTrigger>
                  <SelectContent>
                    {clients.length === 0
                      ? <SelectItem value="__none" disabled>No clients</SelectItem>
                      : clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-600">Site</Label>
                <Select value={form.site_id} onValueChange={(v) => setForm(f => ({ ...f, site_id: v }))} disabled={!form.client_id || !!editingId}>
                  <SelectTrigger data-testid="dlg-site" className="rounded-sm border-slate-300 mt-1"><SelectValue placeholder={form.client_id ? "Pick site" : "Pick client first"} /></SelectTrigger>
                  <SelectContent>
                    {sitesOfSelectedClient.length === 0
                      ? <SelectItem value="__none" disabled>No sites for this client</SelectItem>
                      : sitesOfSelectedClient.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {!form.is_general && (
                <>
                  <div>
                    <Label className="text-xs text-slate-600">Contact type</Label>
                    <Select value={form.contact_type} onValueChange={(v) => setForm(f => ({ ...f, contact_type: v, contact_id: "" }))} disabled={!!editingId}>
                      <SelectTrigger data-testid="dlg-type" className="rounded-sm border-slate-300 mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="subcontractor">Subcontractor</SelectItem>
                        <SelectItem value="self_employed">Self-employed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Contact</Label>
                    <Select value={form.contact_id} onValueChange={(v) => setForm(f => ({ ...f, contact_id: v }))} disabled={!!editingId}>
                      <SelectTrigger data-testid="dlg-contact" className="rounded-sm border-slate-300 mt-1"><SelectValue placeholder="Pick contact" /></SelectTrigger>
                      <SelectContent>
                        {contactOptions.length === 0
                          ? <SelectItem value="__none" disabled>No contacts</SelectItem>
                          : contactOptions.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              <div>
                <Label className="text-xs text-slate-600">Pay rate (£)</Label>
                <Input
                  data-testid="dlg-rate"
                  type="number" min="0" step="0.01"
                  value={form.pay_rate}
                  onChange={(e) => setForm(f => ({ ...f, pay_rate: e.target.value }))}
                  className="rounded-sm border-slate-300 mt-1"
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-600">Unit</Label>
                <Select value={form.unit} onValueChange={(v) => setForm(f => ({ ...f, unit: v }))}>
                  <SelectTrigger data-testid="dlg-unit" className="rounded-sm border-slate-300 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {UNITS.map(u => <SelectItem key={u.v} value={u.v}>{u.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="rounded-sm border border-slate-200 p-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm flex items-center gap-1.5"><Fuel className="w-4 h-4 text-amber-700" /> Fuel included?</Label>
                <Switch
                  data-testid="dlg-fuel-toggle"
                  checked={form.fuel_included}
                  onCheckedChange={(v) => setForm(f => ({ ...f, fuel_included: v }))}
                />
              </div>
              {form.fuel_included && (
                <div className="mt-3">
                  <Label className="text-xs text-slate-600">Fuel allowance £ per day</Label>
                  <Input
                    data-testid="dlg-fuel-amount"
                    type="number" min="0" step="0.01"
                    value={form.fuel_per_day}
                    onChange={(e) => setForm(f => ({ ...f, fuel_per_day: e.target.value }))}
                    className="rounded-sm border-slate-300 mt-1"
                    placeholder="0.00"
                  />
                </div>
              )}
            </div>

            <div>
              <Label className="text-xs text-slate-600">Notes</Label>
              <Input
                data-testid="dlg-notes"
                value={form.notes}
                onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                className="rounded-sm border-slate-300 mt-1"
                placeholder="optional"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={submit} data-testid="dlg-submit" className="rounded-sm bg-slate-900 hover:bg-slate-800">{editingId ? "Save changes" : "Add pay rate"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
